// Schemas for routes-hubspot-calling.ts (browser-safe zod). HubSpot calling extension (plan B6).
//
// HubSpot loads LIA's page `/hubspot/calling` in an iframe on app.hubspot.com (Calling Extensions SDK). Click-to-call
// on a HubSpot record opens it; the call is placed with the advisor's LIA softphone (plan B3) and logged as a HubSpot
// call engagement.
//
// Honesty rules encoded here:
//  • the extension is «sin verificar» until LIA updates, through the HubSpot API, an engagement that a real HubSpot
//    created for a call placed from the extension;
//  • the iframe cannot rely on LIA's session cookie (third-party context): it signs in through a first-party popup and
//    receives a short-lived embed token kept in memory only, valid on the routes listed in HUBSPOT_EMBED_ROUTES;
//  • the developer API key used to register the extension is sent once to HubSpot and never stored.
import { z } from 'zod';

const Iso = z.string();
const Id = z.string().min(1).max(64);

/** SPA route HubSpot frames (calling settings `url`). The only LIA page that may be framed, and only by HubSpot. */
export const HUBSPOT_CALLING_PATH = '/hubspot/calling';
/** First-party popup that signs the embedded page in (never framed). */
export const HUBSPOT_CALLING_CONNECT_PATH = '/hubspot/calling/conectar';
/** postMessage type the popup sends to the embedded page (same origin only). */
export const HUBSPOT_EMBED_TICKET_MESSAGE = 'lia:hubspot-embed-ticket';
/** Prefix of embed tokens (Authorization: Bearer …). */
export const HUBSPOT_EMBED_TOKEN_PREFIX = 'lia_hse_';

/**
 * Origins allowed to frame HUBSPOT_CALLING_PATH (CSP frame-ancestors). HubSpot's app domains per data center
 * (NA1, EU1, NA2, NA3, AP1) — UNVERIFIED list, from HubSpot's public docs; check the first real portal's domain.
 */
export const HUBSPOT_FRAME_ANCESTORS = [
  'https://app.hubspot.com',
  'https://app-eu1.hubspot.com',
  'https://app-na2.hubspot.com',
  'https://app-na3.hubspot.com',
  'https://app-ap1.hubspot.com',
] as const;

/** Default iframe size registered with HubSpot (px). */
export const HUBSPOT_CALLING_SIZE = { width: 400, height: 600 } as const;

/**
 * Routes the embedded page may call with an embed token (Authorization: Bearer lia_hse_…) instead of the session
 * cookie. The token is pinned to one workspace; every other route ignores it.
 */
export const HUBSPOT_EMBED_ROUTES = [
  'getHubspotCallingContext',
  'linkHubspotCallEngagement',
  'endHubspotEmbedSession',
  'getSoftphoneState',
  'mintSoftphoneToken',
  'softphoneHeartbeat',
  'setSoftphonePresence',
  'startSoftphoneCall',
  'hangupSoftphoneCall',
  'transferSoftphoneCall',
] as const;

// ───────── Embed sign-in ─────────

export const HubspotEmbedTicketSchema = z.object({
  /** Single-use, ~2 minutes; the popup hands it to the embedded page with postMessage. */
  ticket: z.string(),
  expiresAt: Iso,
});
export type HubspotEmbedTicket = z.infer<typeof HubspotEmbedTicketSchema>;

export const ExchangeHubspotEmbedTicketSchema = z.object({ ticket: z.string().trim().min(20).max(200) });

export const HubspotEmbedSessionSchema = z.object({
  /** Bearer token for HUBSPOT_EMBED_ROUTES. Keep it in memory only; it dies with the LIA session that created it. */
  token: z.string(),
  expiresAt: Iso,
  workspaceId: Id,
  workspaceName: z.string(),
  user: z.object({ name: z.string(), email: z.string() }),
});
export type HubspotEmbedSession = z.infer<typeof HubspotEmbedSessionSchema>;

// ───────── Context for the embedded page ─────────

export const HubspotCallingContextSchema = z.object({
  workspaceId: Id,
  workspaceName: z.string(),
  /** HubSpot connections of the workspace and their portal ids (engagements only link to these portals). */
  portals: z.array(z.object({ connectionId: Id, portalId: z.string().nullable(), label: z.string(), logCalls: z.boolean() })),
  verification: z.object({ state: z.enum(['verified', 'unverified']), detail: z.string() }),
  notice: z.string(),
});
export type HubspotCallingContext = z.infer<typeof HubspotCallingContextSchema>;

// ───────── Engagement mapping ─────────

export const HUBSPOT_ENGAGEMENT_STATES = ['pending', 'linked', 'synced', 'failed'] as const;
export const HubspotEngagementStateSchema = z.enum(HUBSPOT_ENGAGEMENT_STATES);

export const HUBSPOT_ENGAGEMENT_STATE_LABELS: Record<z.infer<typeof HubspotEngagementStateSchema>, string> = {
  pending: 'Esperando la interacción de HubSpot',
  linked: 'Vinculada',
  synced: 'Actualizada en HubSpot',
  failed: 'No se pudo actualizar',
};

const HubspotNumericId = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .pipe(z.string().regex(/^\d{1,20}$/, 'Identificador de HubSpot inválido'));

export const LinkHubspotCallEngagementSchema = z.object({
  /** HubSpot portal (hub) id reported by the SDK (onReady / onDialNumber). Must belong to a connection of the workspace. */
  portalId: HubspotNumericId,
  /** Engagement id from the SDK (onCreateEngagementSucceeded). Omitted = the call started and HubSpot is creating it. */
  engagementId: HubspotNumericId.optional(),
  /** HubSpot record the advisor dialed from (onDialNumber). */
  objectType: z.string().trim().min(1).max(40).optional(),
  objectId: HubspotNumericId.optional(),
});
export type LinkHubspotCallEngagement = z.input<typeof LinkHubspotCallEngagementSchema>;

export const HubspotCallEngagementSchema = z.object({
  callId: Id,
  connectionId: Id,
  portalId: z.string().nullable(),
  engagementId: z.string().nullable(),
  /** sdk = HubSpot created it from the extension; api = LIA created it through the HubSpot API (writeback). */
  source: z.enum(['sdk', 'api']),
  state: HubspotEngagementStateSchema,
  objectType: z.string().nullable(),
  objectId: z.string().nullable(),
  lastSyncedAt: Iso.nullable(),
  lastError: z.string().nullable(),
  /** The call was already logged with another engagement (kept; the newer one is not linked). */
  conflict: z.string().nullable(),
});
export type HubspotCallEngagement = z.infer<typeof HubspotCallEngagementSchema>;

// ───────── Platform registration (calling settings of the HubSpot public app) ─────────

export const HubspotCallingRegistrationSchema = z.object({
  /** URL HubSpot frames: <WEB_ORIGIN>/hubspot/calling. */
  url: z.string(),
  frameAncestors: z.array(z.string()),
  registration: z
    .object({
      appId: z.string(),
      name: z.string(),
      url: z.string(),
      width: z.number().int(),
      height: z.number().int(),
      isReady: z.boolean(),
      supportsCustomObjects: z.boolean(),
      state: z.enum(['registered', 'failed']),
      detail: z.string().nullable(),
      registeredAt: Iso.nullable(),
      updatedAt: Iso,
    })
    .nullable(),
  verification: z.object({ state: z.enum(['verified', 'unverified']), detail: z.string() }),
  notice: z.string(),
});
export type HubspotCallingRegistration = z.infer<typeof HubspotCallingRegistrationSchema>;

export const RegisterHubspotCallingSchema = z.object({
  appId: z.string().trim().regex(/^\d{1,20}$/, 'El id de la app de HubSpot es numérico'),
  /** Developer API key of the HubSpot developer account that owns the public app. Sent once; never stored. */
  developerApiKey: z.string().trim().min(8).max(200),
  name: z.string().trim().min(2).max(60).default('LIA'),
  width: z.number().int().min(200).max(1200).default(HUBSPOT_CALLING_SIZE.width),
  height: z.number().int().min(300).max(1200).default(HUBSPOT_CALLING_SIZE.height),
  /** false keeps the extension hidden from HubSpot users (only test accounts while it is being set up). */
  isReady: z.boolean().default(false),
  supportsCustomObjects: z.boolean().default(false),
});
export type RegisterHubspotCalling = z.input<typeof RegisterHubspotCallingSchema>;
